import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TruckIcon,
  MapPinIcon,
  PlusIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  PhoneIcon,
  MapIcon,
  InformationCircleIcon,
  CalendarIcon,
  ClockIcon
} from "@heroicons/react/24/outline";

const TransportManagement = () => {
  const [showBookModal, setShowBookModal] = useState(false);
  const [bookingType, setBookingType] = useState("PICKUP"); // Fix for toggle state

  const employeeStats = [
    { label: "My Trips This Month", value: "12", color: "text-indigo-600", bg: "bg-indigo-50", sub: "Limit: 20" },
    { label: "Pending Requests", value: "01", color: "text-amber-600", bg: "bg-amber-50", sub: "Waiting for HR" },
    { label: "Saved Routes", value: "03", color: "text-emerald-600", bg: "bg-emerald-50", sub: "Home, Airport, Hub" },
  ];

  const myBookings = [
    { id: "REQ-4401", type: "OFFICE DROP", route: "Home → Sector 62 Office", time: "30 Dec, 06:00 PM", status: "In Transit", driver: "Suresh Kumar", vehicle: "White Dzire (DL1C-4432)", otp: "5592" },
    { id: "REQ-4390", type: "OFFICE PICKUP", route: "Sector 62 Office → Home", time: "28 Dec, 09:15 AM", status: "Completed", driver: "Rajesh P.", vehicle: "Silver Ertiga (DL8C-1120)", otp: "---" },
    { id: "REQ-4382", type: "OFFICE PICKUP", route: "Office → Airport T3", time: "26 Dec, 11:30 AM", status: "Cancelled", driver: "N/A", vehicle: "N/A", otp: "---" },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 font-['Poppins','sans-serif'] text-gray-800 space-y-4">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-600 p-2.5 rounded-lg text-white">
            <TruckIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 leading-tight">Travell Management</h1>
            <p className="text-[11px] font-['Roboto'] text-gray-500">Employee transport portal for official commute.</p>
          </div>
        </div>
        <button 
            onClick={() => setShowBookModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-[11px] font-bold flex items-center justify-center gap-2 transition-all active:scale-95 uppercase tracking-wider"
        >
            <PlusIcon className="h-4 w-4" /> Request a Ride
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {employeeStats.map((stat, idx) => (
          <div key={idx} className={`${stat.bg} p-3 rounded-lg border border-gray-100 flex items-center justify-between`}>
            <div>
              <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{stat.label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className={`text-xl font-black ${stat.color}`}>{stat.value}</p>
                <p className="text-[9px] font-medium text-gray-400 font-['Roboto']">{stat.sub}</p>
              </div>
            </div>
            <ChevronRightIcon className={`h-4 w-4 ${stat.color} opacity-40`} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* ERP TABULAR LOGS */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/30">
            <h3 className="text-[10px] font-bold uppercase text-gray-600 flex items-center gap-2 tracking-widest">
                <InformationCircleIcon className="h-4 w-4 text-indigo-500" /> Booking History Log
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left font-['Roboto'] border-collapse">
              <thead className="bg-gray-50 text-[9px] uppercase text-gray-400 font-black tracking-wider">
                <tr>
                  <th className="px-4 py-3 border-b border-r border-gray-100">Request ID</th>
                  <th className="px-4 py-3 border-b border-r border-gray-100">Trip Type</th>
                  <th className="px-4 py-3 border-b border-r border-gray-100">Route Details</th>
                  <th className="px-4 py-3 border-b border-r border-gray-100">Driver & Vehicle</th>
                  <th className="px-4 py-3 border-b border-r border-gray-100 text-center">OTP</th>
                  <th className="px-4 py-3 border-b">Status</th>
                </tr>
              </thead>
              <tbody className="text-[11px]">
                {myBookings.map((trip, i) => (
                  <tr key={i} className="hover:bg-indigo-50/20 border-b border-gray-50">
                    <td className="px-4 py-2 border-r border-gray-50 font-bold text-gray-700">{trip.id}</td>
                    <td className="px-4 py-2 border-r border-gray-50">
                      <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{trip.type}</span>
                    </td>
                    <td className="px-4 py-2 border-r border-gray-50">
                      <div className="flex items-center gap-1 font-medium text-gray-700">
                        <MapPinIcon className="h-3 w-3 text-red-400" /> {trip.route}
                      </div>
                      <p className="text-[9px] text-gray-400 mt-0.5">{trip.time}</p>
                    </td>
                    <td className="px-4 py-2 border-r border-gray-50">
                      <p className="font-bold text-gray-600 leading-none">{trip.driver}</p>
                      <p className="text-[9px] text-gray-400 mt-1">{trip.vehicle}</p>
                    </td>
                    <td className="px-4 py-2 border-r border-gray-50 text-center">
                        <span className="bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-700 font-bold border border-gray-200">{trip.otp}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-sm text-[9px] font-bold uppercase border ${
                        trip.status === 'In Transit' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                        trip.status === 'Completed' ? 'bg-green-50 text-green-700 border-green-100' :
                        'bg-red-50 text-red-600 border-red-100'
                      }`}>
                        {trip.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* SIDEBAR TRACKER */}
        <div className="space-y-4">
            <div className="bg-indigo-900 text-white p-4 rounded-xl shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                    <div className="flex justify-between items-center mb-4">
                        <p className="text-[9px] font-bold uppercase tracking-widest opacity-60">Active Tracker</p>
                        <div className="flex gap-1">
                            <span className="h-1.5 w-1.5 bg-green-400 rounded-full animate-pulse"></span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="h-9 w-9 bg-white/10 rounded-lg flex items-center justify-center border border-white/20">
                            <MapIcon className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-xs font-bold">Arriving in 12 Mins</p>
                            <p className="text-[9px] opacity-60">Gate 2, Main Office</p>
                        </div>
                    </div>
                    <button className="w-full bg-white text-indigo-900 py-2 rounded-lg text-[10px] font-bold uppercase tracking-tighter hover:bg-gray-100 transition-all">
                        VIEW ON LIVE MAP
                    </button>
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-[10px] font-bold uppercase text-gray-400 mb-3 flex items-center gap-2 tracking-widest">
                   <ShieldCheckIcon className="h-4 w-4 text-emerald-500" /> Safety Desk
                </h3>
                <div className="space-y-2">
                    <button className="w-full bg-red-50 text-red-600 py-2 rounded-lg text-[10px] font-bold border border-red-100 hover:bg-red-100">SOS EMERGENCY</button>
                    <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3">
                        <PhoneIcon className="h-4 w-4 text-indigo-500" />
                        <div>
                            <p className="text-[10px] font-bold text-gray-700">Helpline</p>
                            <p className="text-[9px] text-gray-400">+91 8800 2211</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* BOOKING MODAL */}
      <AnimatePresence>
        {showBookModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full relative border border-gray-100">
                <div className="mb-5">
                    <h2 className="text-lg font-bold text-gray-900">Request a Ride</h2>
                    <p className="text-[11px] text-gray-400 font-['Roboto']">Employee transport request for official commute.</p>
                </div>
                
                <div className="space-y-4">
                    {/* FIXED TOGGLE LOGIC */}
                    <div className="flex p-1 bg-gray-100 rounded-lg">
                        <button 
                            onClick={() => setBookingType("PICKUP")}
                            className={`flex-1 py-2 text-[10px] font-bold rounded-md transition-all ${bookingType === "PICKUP" ? "bg-white shadow-sm text-indigo-600" : "text-gray-500"}`}
                        >
                            OFFICE PICKUP
                        </button>
                        <button 
                            onClick={() => setBookingType("DROP")}
                            className={`flex-1 py-2 text-[10px] font-bold rounded-md transition-all ${bookingType === "DROP" ? "bg-white shadow-sm text-indigo-600" : "text-gray-500"}`}
                        >
                            OFFICE DROP
                        </button>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[9px] font-bold text-gray-400 uppercase">Date</label>
                        <div className="relative">
                            <CalendarIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input type="date" className="w-full border-gray-200 border rounded-lg p-2.5 pl-9 text-xs outline-none focus:border-indigo-500" />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[9px] font-bold text-gray-400 uppercase">Time Slot</label>
                        <div className="relative">
                            <ClockIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <select className="w-full border-gray-200 border rounded-lg p-2.5 pl-9 text-xs outline-none focus:border-indigo-500 bg-white">
                                <option>09:00 AM</option>
                                <option>02:00 PM</option>
                                <option>06:00 PM</option>
                                <option>09:30 PM</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[9px] font-bold text-gray-400 uppercase">Destination Address</label>
                        <div className="relative">
                            <MapPinIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input type="text" placeholder="Enter drop/pickup location..." className="w-full border-gray-200 border rounded-lg p-2.5 pl-9 text-xs outline-none focus:border-indigo-500" />
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                    <button onClick={() => setShowBookModal(false)} className="w-full bg-indigo-600 text-white py-3 rounded-lg text-xs font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all uppercase tracking-wider">REQUEST CAB NOW</button>
                    <button onClick={() => setShowBookModal(false)} className="w-full py-2 text-[10px] font-bold text-gray-400 hover:text-gray-600 transition-all uppercase tracking-widest">Hold Later</button>
                </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TransportManagement;